import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";

// Props every composer dialog/sheet receives. `session` bumps on each open so the
// dialog re-initializes its form even while it stays mounted between opens.
export interface ComposerDialogProps<Options> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  options: Options | null;
  session: number;
}

// Builds the shared composer plumbing (context + provider + hook) around a
// channel-specific dialog. The email and WhatsApp composers only differ in their
// dialog + options shape, so the provider shell lives here once.
export function createComposer<Options>(
  Dialog: ComponentType<ComposerDialogProps<Options>>,
  hookName: string,
) {
  const Context = createContext<{ open: (options?: Options) => void } | null>(
    null,
  );

  function useComposer() {
    const context = useContext(Context);
    if (!context) {
      throw new Error(`${hookName} must be used within its Provider`);
    }
    return context;
  }

  // Mounted once near the app root so any page can open the single shared dialog.
  function Provider({ children }: { children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<Options | null>(null);
    const [session, setSession] = useState(0);

    const openComposer = useCallback((next?: Options) => {
      setOptions(next ?? null);
      setSession((value) => value + 1);
      setOpen(true);
    }, []);

    return (
      <Context.Provider value={{ open: openComposer }}>
        {children}
        <Dialog
          open={open}
          onOpenChange={setOpen}
          options={options}
          session={session}
        />
      </Context.Provider>
    );
  }

  return { Provider, useComposer };
}
